import {
  AllParams,
  Always,
  AWI,
  Boundary,
  Constraints,
  Density,
  Integers,
  Mute,
  RWI,
  Smoothing,
  StandardEnvironmentVariables,
  Temp,
  TraceManipulation,
} from "../../projects/project/tables/table_params";
import { parameter_view_fields } from "../../projects/project-view/parameter-overview/fields";

/**
 * Editor-agnostic lint marker. Mapped to monaco IMarkerData by applyEditorMarkers().
 * Lines and columns are 1-based, matching monaco's convention.
 */
export interface EditorMarker {
  line: number;
  startColumn: number;
  endColumn: number;
  message: string;
  severity: "error" | "warning";
}

/** Runfile keys are matched case-insensitively with whitespace and underscores ignored. */
export function normalizeRunfileKey(key: string): string {
  return key.toLowerCase().replace(/[\s_]+/g, "");
}

// Parameters known to exist in runfiles but missing from the table lists.
// ITERBLOCK heads each iteration block; its value is the block's name.
const EXTRA_RUNFILE_KEYS = ["Frequency", "Iterations", "ITERBLOCK"];

// Map of normalized key -> display-cased name. Display lists win over the
// snake_case parameter-overview fields and the already-normalized AllParams
// entries so completions look like real runfiles.
const displayByNormalized = new Map<string, string>();
for (const name of [
  ...Always,
  ...Integers,
  ...RWI,
  ...Smoothing,
  ...Mute,
  ...Density,
  ...Temp,
  ...TraceManipulation,
  ...AWI,
  ...Constraints,
  ...Boundary,
  ...EXTRA_RUNFILE_KEYS,
]) {
  displayByNormalized.set(normalizeRunfileKey(name), name);
}
for (const field of Object.values(parameter_view_fields).flat()) {
  const normalized = normalizeRunfileKey(field);
  if (!displayByNormalized.has(normalized)) {
    displayByNormalized.set(normalized, field.replace(/_/g, " "));
  }
}
for (const key of AllParams) {
  if (!displayByNormalized.has(key)) {
    displayByNormalized.set(key, key);
  }
}

export const KNOWN_RUNFILE_KEYS: ReadonlySet<string> = new Set(
  displayByNormalized.keys(),
);

/** Display-cased key names offered by the runfile completion provider. */
export const RUNFILE_KEY_SUGGESTIONS: readonly string[] = [
  ...displayByNormalized.values(),
]
  // "iterations:vs/bg" is a table-display artifact, not a real runfile key
  .filter((k) => !k.includes(":") && !k.includes("/"))
  .sort((a, b) => a.localeCompare(b));


export const KNOWN_ENV_VARS: ReadonlySet<string> = new Set([
  ...StandardEnvironmentVariables,
]);

export const ENV_VAR_SUGGESTIONS: readonly string[] = [
  ...KNOWN_ENV_VARS,
].sort();

function levenshtein(a: string, b: string): number {
  const prev = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    let diagonal = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const insertOrDelete = Math.min(prev[j], prev[j - 1]) + 1;
      const substitute = diagonal + (a[i - 1] === b[j - 1] ? 0 : 1);
      diagonal = prev[j];
      prev[j] = Math.min(insertOrDelete, substitute);
    }
  }
  return prev[b.length];
}

/** Closest candidate within an edit distance that scales with the input length. */
export function closestMatch(
  input: string,
  candidates: Iterable<string>,
): string | null {
  const maxDistance = Math.max(2, Math.floor(input.length / 4));
  let best: string | null = null;
  let bestDistance = maxDistance + 1;
  for (const candidate of candidates) {
    if (Math.abs(candidate.length - input.length) >= bestDistance) continue;
    const distance = levenshtein(input, candidate);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best;
}

/**
 * Flags lines that don't parse as '! comment' or 'Parameter : value', and
 * parameter names that aren't in the known runfile key set.
 * Everything is a warning: the key lists are curated and may lag behind XWI.
 */
export function lintRunfile(content: string): EditorMarker[] {
  const markers: EditorMarker[] = [];
  if (!content) return markers;
  content.split("\n").forEach((rawLine, idx) => {
    const line = idx + 1;
    const trimmed = rawLine.trim();
    if (!trimmed || trimmed.startsWith("!")) return;
    const colon = rawLine.indexOf(":");
    if (colon === -1) {
      markers.push({
        line,
        startColumn: 1,
        endColumn: rawLine.length + 1,
        severity: "warning",
        message:
          "Line is not a comment ('!') and does not look like 'Parameter : value' — missing colon?",
      });
      return;
    }
    const key = rawLine.slice(0, colon).trim();
    if (!key) {
      markers.push({
        line,
        startColumn: 1,
        endColumn: colon + 2,
        severity: "warning",
        message: "Missing parameter name before ':'.",
      });
      return;
    }
    const normalized = normalizeRunfileKey(key);
    if (!KNOWN_RUNFILE_KEYS.has(normalized)) {
      const match = closestMatch(normalized, KNOWN_RUNFILE_KEYS);
      const suggestion = match ? displayByNormalized.get(match) : null;
      const startColumn = rawLine.indexOf(key) + 1;
      markers.push({
        line,
        startColumn,
        endColumn: startColumn + key.length,
        severity: "warning",
        message:
          `Unknown parameter '${key}'.` +
          (suggestion ? ` Did you mean '${suggestion}'?` : ""),
      });
    }
  });
  return markers;
}

/**
 * Flags SCHEDULER_* / SLAVES_* assignments whose name isn't a known XWI
 * environment variable. Other assignments and arbitrary shell are left alone —
 * env.sh is a sourced script and may legitimately contain anything.
 */
export function lintEnvSh(content: string): EditorMarker[] {
  const markers: EditorMarker[] = [];
  if (!content) return markers;
  content.split("\n").forEach((rawLine, idx) => {
    const match = rawLine.match(/^\s*(?:export\s+)?([A-Za-z_][A-Za-z0-9_]*)=/);
    if (!match) return;
    const name = match[1];
    const upper = name.toUpperCase();
    if (!upper.startsWith("SCHEDULER_") && !upper.startsWith("SLAVES_")) {
      return;
    }
    const startColumn = match[0].length - name.length;
    const endColumn = startColumn + name.length;
    if (KNOWN_ENV_VARS.has(name)) return;
    if (KNOWN_ENV_VARS.has(upper)) {
      markers.push({
        line: idx + 1,
        startColumn,
        endColumn,
        severity: "warning",
        message: `'${name}' should be uppercase: '${upper}'.`,
      });
      return;
    }
    const suggestion = closestMatch(upper, KNOWN_ENV_VARS);
    markers.push({
      line: idx + 1,
      startColumn,
      endColumn,
      severity: "warning",
      message:
        `Unknown environment variable '${name}'.` +
        (suggestion ? ` Did you mean '${suggestion}'?` : ""),
    });
  });
  return markers;
}

/** Pushes lint markers onto a monaco text model as warning/error squiggles. */
export function applyEditorMarkers(
  model: unknown,
  markers: EditorMarker[],
): void {
  const monaco = (window as any).monaco;
  if (!monaco || !model) return;
  monaco.editor.setModelMarkers(
    model,
    "scube-lint",
    markers.map((m) => ({
      severity:
        m.severity === "error"
          ? monaco.MarkerSeverity.Error
          : monaco.MarkerSeverity.Warning,
      message: m.message,
      startLineNumber: m.line,
      endLineNumber: m.line,
      startColumn: m.startColumn,
      endColumn: m.endColumn,
    })),
  );
}
